import type { TaskTypeConfig, TaskTypesConfig } from "@/types";

/**
 * Parse a due days input value.
 * Returns the parsed number, undefined (for empty/clear), or null if invalid.
 */
export const parseDueDays = (value: string): number | undefined | null => {
  if (value === "") return undefined;
  const num = parseInt(value, 10);
  if (isNaN(num)) return null;
  return num;
};

/**
 * Parse a threshold override input value (1–100 integer).
 * Returns the string value, undefined (for empty/clear), or null if invalid.
 */
export const parseThresholdOverride = (value: string): string | undefined | null => {
  if (value === "") return undefined;
  const num = parseInt(value, 10);
  if (isNaN(num) || num < 1 || num > 100) return null;
  return String(num);
};

/**
 * Format a success rate for display.
 * Returns "—" for NaN/null/undefined, otherwise rounds to nearest integer with %.
 */
export const formatSuccessRate = (rate: number | null | undefined): string => {
  if (rate === null || rate === undefined || isNaN(rate)) return "—";
  return `${Math.round(rate)}%`;
};

/**
 * Resolve effective enabled state for a partner task type.
 * Partner override → global template is_active → default true.
 */
export const resolveEffectiveEnabled = (
  configEnabled: boolean | undefined,
  templateIsActive: boolean | undefined
): boolean => {
  return configEnabled ?? templateIsActive ?? true;
};

/**
 * Build the updated partner configuration object with a task type config change.
 * Returns the full configuration object ready for a partner update.
 */
export const buildPartnerTaskTypeUpdate = (
  currentConfig: Record<string, unknown> | undefined,
  taskType: string,
  updates: Partial<TaskTypeConfig>
): { task_types: TaskTypesConfig } & Record<string, unknown> => {
  const currentTaskTypes = (currentConfig?.task_types ?? {}) as TaskTypesConfig;
  const updatedConfig: TaskTypeConfig = {
    ...currentTaskTypes[taskType],
    ...updates,
  };

  return {
    ...currentConfig,
    task_types: {
      ...currentTaskTypes,
      [taskType]: updatedConfig,
    },
  };
};

/**
 * Whether a template supports threshold override (only threshold_monitoring category).
 */
export const isThresholdTemplate = (templateCategory: string | undefined): boolean => {
  return templateCategory === "threshold_monitoring";
};
